import { produce } from 'immer'
import { StateCreator } from 'zustand'
import { UserInfoDto } from '@/api/origin-user-mgmt-svc'
import { TitleAndGrantStatus } from '@/api/origin-workflow-svc'
import type { IStore } from '@/state/store'
import { getUserDetails } from '@/services/user'
import { logout } from '@/utils/auth'

export enum AuthStatus {
  Loading,
  SignedIn,
  SignedOut
}
export enum AccountStatus {
  INVITED = 'INVITED',
  ONBOARDING_STARTED = 'ONBOARDING_STARTED',
  PASSCODE_PENDING = 'PASSCODE_PENDING',
  VERIFICATION_PENDING = 'VERIFICATION_PENDING',
  ONBOARDING_COMPLETE = 'ONBOARDING_COMPLETE',
  GET_STARTED_WORKFLOW_COMPLETE = 'GET_STARTED_WORKFLOW_COMPLETE'
}

export type UserInfo = {
  id: string
  sub?: string
  firstName?: string
  lastName?: string
  emails: {
    value: string
  }[]
  phoneNumbers?: {
    value: string
    countryCode: string
  }[]
  orgs?: {
    id: string
    name: string
    domain: string
  }[]
  grants?: string[]
}

export type Authentication = {
  authStatus: AuthStatus
  userInfo: UserInfoDto | null
}

export type AuthenticationStore = {
  authentication: Authentication
  setSession: (userInfo?: UserInfoDto) => void
  killSession: () => void
  setLoginUser: (userDetails: UserInfoDto | null) => void
  replaceGrantWithShortTitle: (titleAndGrant: TitleAndGrantStatus) => void
  resetAuthenticationStore: () => void
  updateUserDetails: () => Promise<void>
}

const initialState = {
  authentication: {
    authStatus: AuthStatus.Loading,
    userInfo: null
  }
}

const authenticationStore: StateCreator<IStore, [], [], AuthenticationStore> = (
  set,
  get
) => ({
  ...initialState,

  killSession: () => {
    logout()
    set(() => ({
      authentication: {
        authStatus: AuthStatus.SignedOut,
        userInfo: null
      }
    }))
  },
  setSession: async (userInfo) => {
    if (get().authentication.authStatus === AuthStatus.SignedIn) {
      return
    }

    // localStorage.getItem may return null; guard before JSON.parse.
    const stored = localStorage.getItem('currentUser')
    const usrInfo: UserInfoDto | undefined =
      userInfo || (stored ? JSON.parse(stored) : undefined)

    if (!usrInfo) {
      return get().killSession()
    }

    localStorage.setItem('currentUser', JSON.stringify(usrInfo))
    set(() => ({
      authentication: {
        authStatus: AuthStatus.SignedIn,
        userInfo: usrInfo
      }
    }))
  },
  setLoginUser: (newUserInfo) => {
    newUserInfo &&
      localStorage.setItem('currentUser', JSON.stringify(newUserInfo))
    set((state) => ({
      authentication: {
        authStatus:
          newUserInfo !== null ? AuthStatus.SignedIn : AuthStatus.SignedOut,
        userInfo: {
          ...state.authentication.userInfo,
          ...newUserInfo
        }
      }
    }))
  },
  replaceGrantWithShortTitle: (titleAndGrant) => {
    set(
      produce((state: AuthenticationStore) => {
        const userInfo = state.authentication.userInfo
        if (!userInfo || !userInfo.grants) return
        const title = titleAndGrant.title
        if (!title) return
        const index = userInfo.grants.findIndex(
          (grant) => grant.toUpperCase() === title.toUpperCase()
        )
        if (index > -1) {
          userInfo.grants[index] = titleAndGrant.shortTitle || title
        }
      })
    )
  },
  updateUserDetails: async () => {
    const userDetails = await getUserDetails()
    if (!userDetails) return
    if (userDetails.grants) {
      userDetails.grants = userDetails.grants.map((g) => g.toUpperCase())
    }
    get().setLoginUser(userDetails)
  },
  resetAuthenticationStore: () => {
    set(initialState)
  }
})

/**
 * Hub helper (M4.4 Step A) — returns the current authenticated userInfo,
 * or throws if no user is signed in. Mirrors the `clientOrThrow()` pattern
 * introduced in `src/state/signify.ts` (M4.3) for `SignifyClient | null`.
 *
 * Use this from non-React helpers that require an authenticated user.
 * For React components/hooks, prefer reading `userInfo` from the store
 * with a guard, since rendering during the Loading auth state is normal.
 *
 * Throws synchronously — call sites should be inside try/catch or
 * inside an effect that only runs after `authStatus === SignedIn`.
 */
export function getUserInfoOrThrow(store: {
  authentication: Authentication
}): UserInfoDto {
  const { userInfo } = store.authentication
  if (!userInfo) {
    throw new Error(
      'getUserInfoOrThrow: no authenticated user — call only when authStatus is SignedIn'
    )
  }
  return userInfo
}

/**
 * Hub helper (M4.4 Step A) — typed narrowing for the common pattern of
 * "the caller already proved the user is signed in earlier in this scope,
 * but the store still types userInfo as nullable". Returns the userInfo
 * if non-null, otherwise undefined — callers should pair this with an
 * early-return guard.
 */
export function userInfoOf(store: {
  authentication: Authentication
}): UserInfoDto | undefined {
  return store.authentication.userInfo ?? undefined
}

export default authenticationStore
